import type { AppProps } from 'next/app';
import React, { Fragment } from 'react';
import ReactGA from 'react-ga';

import GDPRBanner from '@/components/GDPRBanner';
import ToastContainer from '@/components/Toasts';
import { RecordingOverlay, RecordingPreviewDialog } from '@/containers/screen-record';
import ConfirmationProvider from '@/context/ConfirmationDialogContext';
import ErrorGate, { type ErrorGateSSRProps } from '@/context/ErrorContext';
import AppContextWrapper from '@/context/RootContext';
import { ToastProvider } from '@/context/ToastContext';
import { AutofillInfoProvider } from '@/contexts/AutofillInfoContext';
import { usePreventNumberInputScroll } from '@/hooks';
import { BaseLayout, getNoLayout } from '@/layouts';
import {
  CartProvider,
  CreatorUIProvider,
  ErrorBoundary,
  ErrorContext,
  EventsProvider,
  FeatureFlagProvider,
  GladeRedirectProvider,
  HistoryProvider,
  LoomProvider,
  PostHogProvider,
  SEOProvider,
  SocketProvider,
  SwrCache,
  UiProvider,
} from '@/providers';
import AgencyProvider from '@/providers/Agency/AgencyProvider';
import AuthProvider from '@/providers/Auth/AuthProvider';
import IframeAdjustments from '@/providers/IframeAdjustments';
import { JobProvider } from '@/providers/Jobs';
import { ScreenRecordProvider } from '@/providers/ScreenRecord';
import { type NoodlePage } from '@/typings/NextJS';
import '@/polyfills/Promise.withResolvers';

import { type SeoSSRProps } from '@/providers/SEO';
import MixpanelProvider from '@/providers/Mixpanel/MixpanelProvider';
import MessagesProvider from '@/providers/Messages/MessagesProvider';
import NuqsProvider from '@/providers/Nuqs';

import { type FeatureBundle, LazyMotion, MotionConfig } from 'motion/react';

import PWAProvider from '@/providers/PWA';
import TeamsProvider from '@/providers/Teams/TeamsProvider';
import LocalStorageStateProvider from '@/providers/LocalStorageState';
import PushNotificationProvider from '@/providers/PushNotifications';
import { TranslationProvider } from '@/providers/TranslationProvider';
import { FilingProgressProvider } from '@/providers/FilingProgress';
import { RegeneratingDocumentsProvider } from '@/providers/RegeneratingDocuments';
import '@/styles/globals.scss';
import '@/ui/theme/index.css';

const loadFramerFeatures = (): Promise<FeatureBundle> =>
  import('@/helpers/getFramerMotionFeatures').then((res) => res.default);

type ProviderProps = SeoSSRProps & ErrorGateSSRProps;

type NoodleAppProps = AppProps & {
  Component: NoodlePage;
};

if (typeof window !== 'undefined') {
  ReactGA.initialize(process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || '');
  ReactGA.plugin.require('ec');
}

const NoodleApp: React.FC<NoodleAppProps> = ({ Component, pageProps }) => {
  const Layout: NonNullable<NoodlePage['Layout']> = Component.Layout || BaseLayout;
  const getLayout = Component.getLayout || getNoLayout;

  usePreventNumberInputScroll();

  const { ssrError, seo, ...restProps } = pageProps as ProviderProps;

  return (
    <Fragment>
      <ErrorBoundary>
        <UiProvider>
          <LazyMotion features={loadFramerFeatures}>
            <ToastProvider>
              <EventsProvider>
                <JobProvider>
                  <SEOProvider seo={seo} />
                  <NuqsProvider>
                    <AuthProvider>
                      <SwrCache>
                        <MixpanelProvider />
                        <PostHogProvider>
                          <LoomProvider>
                            <ScreenRecordProvider>
                              <RecordingOverlay />
                              <RecordingPreviewDialog />
                              <GladeRedirectProvider>
                                <AgencyProvider>
                                  <TeamsProvider>
                                    <LocalStorageStateProvider />
                                    <FeatureFlagProvider>
                                      <AppContextWrapper>
                                        <ErrorGate ssrError={ssrError}>
                                          <PWAProvider>
                                            <PushNotificationProvider>
                                              <IframeAdjustments />
                                              <ErrorContext />
                                              <CartProvider />
                                              <CreatorUIProvider>
                                                <HistoryProvider>
                                                  <SocketProvider>
                                                    <FilingProgressProvider>
                                                      <RegeneratingDocumentsProvider>
                                                        <MessagesProvider>
                                                          <TranslationProvider>
                                                            <ConfirmationProvider>
                                                              <AutofillInfoProvider>
                                                                <Layout {...restProps}>
                                                                  <MotionConfig transition={{ duration: 0.2 }}>
                                                                    {getLayout(<Component {...pageProps} />)}
                                                                    <GDPRBanner />
                                                                  </MotionConfig>
                                                                </Layout>
                                                              </AutofillInfoProvider>
                                                            </ConfirmationProvider>
                                                          </TranslationProvider>
                                                        </MessagesProvider>
                                                      </RegeneratingDocumentsProvider>
                                                    </FilingProgressProvider>
                                                  </SocketProvider>
                                                </HistoryProvider>
                                              </CreatorUIProvider>
                                            </PushNotificationProvider>
                                          </PWAProvider>
                                        </ErrorGate>
                                      </AppContextWrapper>
                                    </FeatureFlagProvider>
                                  </TeamsProvider>
                                </AgencyProvider>
                              </GladeRedirectProvider>
                            </ScreenRecordProvider>
                          </LoomProvider>
                        </PostHogProvider>
                      </SwrCache>
                    </AuthProvider>
                  </NuqsProvider>
                  <ToastContainer />
                </JobProvider>
              </EventsProvider>
            </ToastProvider>
          </LazyMotion>
        </UiProvider>
      </ErrorBoundary>
    </Fragment>
  );
};

export default NoodleApp;

// Not looking at results and causing errors sometimes.

// const canSendBeacon = (
//   typeof document !== 'undefined'
//   && configuration.ENABLE_WEB_VITALS
//   && window?.location && window.location.protocol === 'https:'
//   && navigator && navigator.sendBeacon
// );
// const sendBeacon = canSendBeacon ? navigator.sendBeacon.bind(navigator) : null;

// export function reportWebVitals(metric: NextWebVitalsMetric): void {
//   const body = JSON.stringify(metric);
//   const url = '/api/health-checks/web-vitals';

//   // Use `navigator.sendBeacon()` if available, falling back to `fetch()`.
//   if (sendBeacon) {
//     try {
//       sendBeacon(url, body);
//     } catch (_err) {
//       /* noop */
//     }
//   } else if (fetch) {
//     fetch(url, { body, keepalive: true, method: 'POST' }).catch(() => {
//       /* noop */
//     });
//   }
// }
